<template>

  <modalo
    :title="careersite.name"
    :type="generateWithAI ? 'small' : 'mini'"
    @hideModal="hideModal()"
    :modaloWatch="() => careersite">

    <form
      id="careersite-form"
      :class="{'box-loading': loading}"
      v-on:submit.prevent="saveCareersite(careersite)">
      <div class="form-group">
        <label>* {{ $t('Name') }}</label>
        <input v-autofocus type="text" class="form-control" v-model="careersite.name" />
        <ValidateMessage :error="validateErrors.name" />
      </div>

      <div class="form-group">
        <label>{{ $t('Slug') }}</label>
        <input
          type="text"
          class="form-control"
          v-model="careersite.slug"
          @input="slugEdited = true" />
        <ValidateMessage :error="validateErrors.slug" />
      </div>

      <div
        v-if="!careersite._id"
        class="form-group">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            id="generateWithAI"
            v-model="generateWithAI">
          <label
            class="form-check-label"
            for="generateWithAI">
            {{ $t('Gerar preset com IA') }}
          </label>
        </div>
        <p class="text-muted mb-0">{{ $t('A IA usa o site, as redes sociais, o logo, as fotos e as informações da empresa para montar o site de carreiras automaticamente.') }}</p>
      </div>

      <div v-if="generateWithAI && !careersite._id">

        <hr />

        <div class="alert alert-info">
          <p class="mb-2">
            <i class="fas fa-info-circle mr-1" />
            <strong>{{ $t('Como funciona a geração com IA') }}</strong>
          </p>
          <p class="mb-2">{{ $t('A IA realiza uma pesquisa aprofundada em todas as fontes informadas e levanta dados como setor de atuação, identidade visual, equipe, localização, cultura e benefícios, entre outras informações relevantes. Com base nisso, gera uma versão inicial do site de carreiras, pronta para ser revisada e ajustada no editor.') }}</p>
          <p class="mb-2">{{ $t('As imagens do site podem ser geradas por IA a partir das referências visuais encontradas nas fontes, para que o layout já seja entregue preenchido.') }}</p>
          <p class="mb-0">{{ $t('Recomendamos fortemente substituir, ao final, as imagens geradas por IA por fotos reais da empresa, sempre que disponíveis, para garantir autenticidade e credibilidade ao site.') }}</p>
        </div>

        <h6 class="mb-1">{{ $t('Presença online') }}</h6>
        <p class="text-muted mb-3">{{ $t('Esses canais são a principal fonte de pesquisa da IA. Quanto mais fontes informadas, mais precisa será a leitura.') }}</p>

        <div class="form-group">
          <label>* {{ $t('Site da empresa') }}</label>
          <input
            type="url"
            class="form-control"
            placeholder="https://"
            v-model="careersite.ai_preset.website" />
          <ValidateMessage :error="validateErrors['ai_preset.website']" />
        </div>

        <div class="form-group">
          <label>{{ $t('Site de carreiras atual') }}</label>
          <input
            type="url"
            class="form-control"
            placeholder="https://"
            v-model="careersite.ai_preset.current_careersite" />
          <ValidateMessage :error="validateErrors['ai_preset.current_careersite']" />
        </div>

        <div class="form-group">
          <label>LinkedIn</label>
          <input
            type="url"
            class="form-control"
            placeholder="https://linkedin.com/company/..."
            v-model="careersite.ai_preset.linkedin" />
          <ValidateMessage :error="validateErrors['ai_preset.linkedin']" />
        </div>

        <div class="form-group">
          <label>Instagram</label>
          <input
            type="url"
            class="form-control"
            placeholder="https://instagram.com/..."
            v-model="careersite.ai_preset.instagram" />
          <ValidateMessage :error="validateErrors['ai_preset.instagram']" />
        </div>

        <div class="form-group">
          <label>Facebook</label>
          <input
            type="url"
            class="form-control"
            placeholder="https://facebook.com/..."
            v-model="careersite.ai_preset.facebook" />
          <ValidateMessage :error="validateErrors['ai_preset.facebook']" />
        </div>

        <div class="form-group">
          <label>YouTube</label>
          <input
            type="url"
            class="form-control"
            placeholder="https://youtube.com/@..."
            v-model="careersite.ai_preset.youtube" />
          <ValidateMessage :error="validateErrors['ai_preset.youtube']" />
        </div>

        <div class="form-group">
          <label>{{ $t('Outros sites') }}</label>
          <div
            v-for="(site, index) in careersite.ai_preset.other_sites"
            :key="index"
            class="input-group mb-2">
            <input
              type="url"
              class="form-control"
              placeholder="https://"
              v-model="careersite.ai_preset.other_sites[index]" />
            <div class="input-group-append">
              <button
                type="button"
                class="btn btn-light"
                @click="removeOtherSite(index)">
                <i class="fas fa-times" />
              </button>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-light"
            @click="addOtherSite()">
            <i class="fas fa-plus" /> {{ $t('Adicionar site') }}
          </button>
          <ValidateMessage :error="validateErrors['ai_preset.other_sites']" />
        </div>

        <hr />

        <h6 class="mb-1">{{ $t('Identidade visual') }}</h6>
        <p class="text-muted mb-3">{{ $t('O logo e as fotos orientam a paleta de cores, a tipografia e o estilo visual do site, e servem de referência para as imagens geradas por IA, mantendo o resultado alinhado à marca da empresa.') }}</p>

        <div class="form-group">
          <label>{{ $t('Logo') }}</label>
          <div class="d-flex align-items-center">
            <img
              v-if="logo"
              :src="logo.url"
              class="logo-preview mr-3" />
            <InputOnePhoto
              :value="logo"
              :selectLabel="$t('Selecionar logo')"
              :removeOption="true"
              @input="setLogo" />
          </div>
          <ValidateMessage :error="validateErrors['ai_preset.logo_media_id']" />
        </div>

        <div class="form-group">
          <label>{{ $t('Fotos') }}</label>
          <div
            v-if="images.length"
            class="images mb-2">
            <div
              v-for="image in images"
              :key="image._id"
              class="image">
              <img :src="image.url" />
              <a
                href="javascript:void(0)"
                class="remove"
                @click="removeImage(image)">
                <i class="fas fa-times" />
              </a>
            </div>
          </div>
          <InputDropMedia
            type="image"
            @input="addImages" />
          <ValidateMessage :error="validateErrors['ai_preset.image_media_ids']" />
        </div>

        <hr />

        <h6 class="mb-1">{{ $t('Informações da empresa') }}</h6>
        <p class="text-muted mb-3">{{ $t('As informações aqui são usadas pela IA para montar as seções do site de carreiras (benefícios, cultura, depoimentos, unidades, etc.).') }}</p>

        <div class="form-group">
          <textarea
            class="form-control"
            rows="18"
            :placeholder="companyInfoPlaceholder"
            v-model="careersite.ai_preset.company_info"></textarea>
          <ValidateMessage :error="validateErrors['ai_preset.company_info']" />
        </div>

      </div>
    </form>

    <template slot="footer">
      <div :class="{'box-loading': loading}">
        <button
          type="submit"
          form="careersite-form"
          class="btn btn-primary">{{ generateWithAI && !careersite._id ? $t('Gerar e salvar') : $t('Save') }}</button>
        <button
          type="button"
          class="btn btn-light"
          @click="hideModal()">{{ $t('Cancel') }}</button>
        <button
          v-if="careersite._id"
          type="button"
          class="btn btn-danger"
          :disabled="careersite.total_jobs > 0"
          v-tooltip="careersite.total_jobs > 0 ? 'Existem vagas associadas a este site de carreiras' : null"
          @click="deleteCareersite(careersite._id)">{{ $t('Delete') }}</button>
      </div>
    </template>

  </modalo>

</template>

<script>
import InputOnePhoto from '@/components/Fields/InputOnePhoto'
import InputDropMedia from '@/components/Fields/InputDropMedia'

export default {
  name: 'CareersiteForm',
  components: {
    InputOnePhoto,
    InputDropMedia
  },
  props: [
    'initCareersite'
  ],
  data () {
    return {
      loading: false,
      generateWithAI: !this.initCareersite._id,
      slugEdited: false,
      logo: null,
      images: [],
      careersite: this.defaultValues(this.initCareersite),
      validateErrors: {}
    }
  },
  computed: {
    companyInfoPlaceholder () {
      return [
        'Escreva em texto livre. Os tópicos abaixo são só um guia, use os que fizerem sentido.',
        '',
        'BENEFÍCIOS',
        'Exemplo: Plano de saúde e odontológico sem coparticipação, vale refeição, Gympass, day off no aniversário, auxílio home office.',
        '',
        'DEPOIMENTOS (nome, cargo e frase literal)',
        'Exemplo: "Entrei como estagiária e hoje lidero um time de 8 pessoas." — Ana Souza, Coordenadora de Marketing',
        'Exemplo: "O que mais gosto é a liberdade para propor ideias." — Carlos Lima, Analista de Logística',
        '',
        'CULTURA E VALORES',
        'Exemplo: Como é o dia a dia, o que a empresa valoriza, rituais (reunião semanal de resultados, happy hour mensal, café com o CEO).',
        '',
        'ÁREAS E EQUIPES',
        'Exemplo: Comercial, Operações, Tecnologia, Atendimento. O que cada área faz.',
        '',
        'PROGRAMAS',
        'Exemplo: Estágio, trainee, jovem aprendiz, programa de inclusão, bolsa de estudos.',
        '',
        'UNIDADES',
        'Exemplo: Matriz em São Paulo (Vila Olímpia), fábrica em Jundiaí, filiais em Curitiba e Recife.',
        '',
        'NÚMEROS E CONQUISTAS',
        'Exemplo: Fundada em 2008, certificação GPTW, prêmios e reconhecimentos do setor.',
        '',
        'OUTRAS INFORMAÇÕES',
        'Exemplo: Qualquer coisa que deva aparecer no site: processo seletivo, frase de posicionamento, vídeo institucional (link).'
      ].join('\n')
    }
  },
  watch: {
    'careersite.name' (name) {
      // Only auto-generate the slug when creating a new site and the
      // user has not manually edited the slug field.
      if (this.careersite._id || this.slugEdited) return
      this.careersite.slug = this.slugify(name)
    }
  },
  created () {
  },
  methods: {
    /*
    slugify
    */
    slugify (value) {
      return (value || '')
        .toString()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },
    /*
    defaultValues
    */
    defaultValues (values) {
      return values._id
      ? this.$clone(values)
      : Object.assign({
        name: null,
        slug: null,
        ai_preset: {
          website: null,
          current_careersite: null,
          linkedin: null,
          instagram: null,
          facebook: null,
          youtube: null,
          other_sites: [],
          logo_media_id: null,
          image_media_ids: [],
          company_info: null
        }
      }, values)
    },
    /*
    ai preset: other sites
    */
    addOtherSite () {
      this.careersite.ai_preset.other_sites.push('')
    },
    removeOtherSite (index) {
      this.careersite.ai_preset.other_sites.splice(index, 1)
    },
    /*
    ai preset: logo
    */
    setLogo (media) {
      this.logo = media
      this.careersite.ai_preset.logo_media_id = media ? media._id : null
    },
    /*
    ai preset: images
    */
    addImages (medias) {
      this.images = this.images.concat(medias)
      this.careersite.ai_preset.image_media_ids = this.images.map(image => image._id)
    },
    removeImage (image) {
      this.images = this.images.filter(item => item._id !== image._id)
      this.careersite.ai_preset.image_media_ids = this.images.map(item => item._id)
    },
    /*
    hideModal
    */
    hideModal () {
      this.$emit('hideModal')
    },
    /*
    saveCareersite
    */
    saveCareersite (data) {
      if (data._id) this.updateCareersite(data)
      else this.createCareersite(data)
    },
    /*
    createCareersite
    */
    createCareersite (data) {
      this.loading = true

      const payload = this.$clone(data)

      if (this.generateWithAI) {
        payload.ai_preset = {
          ...payload.ai_preset,
          other_sites: (payload.ai_preset.other_sites || []).filter(site => site && site.trim())
        }
      } else {
        delete payload.ai_preset
      }

      this.$store
      .dispatch('createCareersite', payload)
      .then((result) => {
        this.loading = false
        this.$emit('saved')
      })
      .catch((err) => {
        this.loading = false
        if (err.response.data.name === 'ValidationError') {
          this.validateErrors = err.response.data.errors
        }
      })
    },
    /*
    updateCareersite
    */
    updateCareersite (data) {
      this.loading = true

      this.$store
      .dispatch('updateCareersite', data)
      .then((result) => {
        this.loading = false
        this.$emit('saved')
      })
      .catch((err) => {
        this.loading = false
        if (err.response.data.name === 'ValidationError') {
          this.validateErrors = err.response.data.errors
        }
      })
    },
    /*
    delete
    */
    deleteCareersite (id) {
      const deleteAction = () => {
        this.loading = true

        this.$store
        .dispatch('deleteCareersite', id)
        .then((result) => {
          this.loading = false
          this.$emit('saved')
        })
      }

      this.$store
      .dispatch('setAlertModal', {
        action: deleteAction
      })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.logo-preview {
  max-height: 60px;
  max-width: 160px;
  object-fit: contain;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px;
  background: #ffffff;
}
.images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
  .images .image {
    position: relative;
    width: 90px;
    height: 90px;
  }
  .images .image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #dee2e6;
  }
  .images .image .remove {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 20px;
    height: 20px;
    line-height: 20px;
    text-align: center;
    font-size: 11px;
    border-radius: 50%;
    background: #ffffff;
    border: 1px solid #dee2e6;
    color: #dc3545;
  }
</style>



// WEBPACK FOOTER //
// src/components/Careersite/CareersiteForm.vue